/**
 * Connects privacy choices to Google Analytics, HubSpot, PostHog, and Sentry.
 * Coordinates starting, stopping, and page refreshes when choices or accounts
 * change, so the tools follow the current visitor's permission.
 */
import { watch } from "vue"
import * as Sentry from "@sentry/vue"
import type { usePrivacyStore } from "@/stores/privacyStore"
import { createPrivacyChoices, hasWithdrawnPermission, TrackingPurpose } from "@/types/privacyChoices"
import { posthogAnalyticsAdapter } from "@/utils/posthogAnalyticsAdapter"
import { applyGoogleAnalyticsChoice, stopGoogleAnalytics } from "@/utils/googleAnalytics"
import { applyHubSpotTrackingChoice, stopHubSpotTracking } from "@/utils/hubspotTracking"
import { isPrivateRoute } from "@/utils/trackingLocation"
import { refreshWithPrivacyChoices } from "@/utils/privacyStorage"
import { stopOptionalSentryTelemetry } from "@/utils/sentryPrivacyTransport"

export function startPrivacyTracking(
	store: ReturnType<typeof usePrivacyStore>,
	getScope: () => string,
	identifyCurrentUser: () => void,
	getRoute: () => string,
	canReload: () => boolean = () => true,
	reload: (forceDecline: boolean) => void = refreshWithPrivacyChoices
): () => void {
	const disposeStorage = store.initialize(getScope())
	let applied = createPrivacyChoices()
	let active = false
	let reloading = false
	let pendingReload = false
	let replayStarted = false

	function stopReplay(): void {
		try {
			const replay = Sentry.getReplay()
			if (replay && (replayStarted || replay.getReplayId())) {
				replayStarted = false
				void replay.stop().catch(error => console.warn("Could not stop session recording", error))
			}
		} catch (error) {
			console.warn("Could not stop session recording", error)
		}
	}

	function applyChoices(): void {
		if (reloading) return
		const selected = isPrivateRoute() ? createPrivacyChoices() : { ...store.choices }
		const mustReload = active && hasWithdrawnPermission(applied, selected)
		if (mustReload) {
			stopOptionalSentryTelemetry()
			stopGoogleAnalytics()
			stopHubSpotTracking()
		}
		pendingReload ||= mustReload
		applied = selected
		if (pendingReload) {
			// Never start new work in a runtime carrying the previous account's
			// identity or queues, even if a later choice grants permission again.
			posthogAnalyticsAdapter.applyPrivacyChoices(false, false)
			stopReplay()
			if (canReload()) {
				reloading = true
				reload(store.storageError)
			}
			return
		}
		const posthogStarted = posthogAnalyticsAdapter.applyPrivacyChoices(
			selected[TrackingPurpose.UsageStatistics],
			selected[TrackingPurpose.SessionRecording]
		)
		if (posthogStarted) identifyCurrentUser()
		if (selected[TrackingPurpose.SessionRecording]) {
			try {
				const replay = Sentry.getReplay()
				if (replay && !replayStarted) {
					replayStarted = true
					if (!replay.getReplayId()) replay.start()
				}
			} catch (error) {
				replayStarted = false
				console.warn("Could not start session recording", error)
			}
		} else {
			stopReplay()
		}
		let analyticsStarted = false
		try {
			analyticsStarted = applyGoogleAnalyticsChoice(selected[TrackingPurpose.UsageStatistics])
		} catch (error) {
			console.warn("Could not start Google Analytics", error)
		}
		let hubspotStarted = false
		try {
			hubspotStarted = applyHubSpotTrackingChoice(selected[TrackingPurpose.UsageStatistics])
		} catch (error) {
			console.warn("Could not start HubSpot tracking", error)
		}
		active ||=
			selected[TrackingPurpose.UsageStatistics] ||
			selected[TrackingPurpose.SessionRecording] ||
			posthogStarted ||
			analyticsStarted ||
			hubspotStarted
	}

	const stopScopeWatch = watch(getScope, value => store.changeScope(value), { flush: "sync" })
	const stopChoiceWatch = watch(() => store.choices, applyChoices, { immediate: true, flush: "sync" })
	const stopRouteWatch = watch(getRoute, applyChoices, { flush: "post" })
	const stopReloadWatch = watch(canReload, applyChoices, { flush: "sync" })
	return () => {
		stopScopeWatch()
		stopChoiceWatch()
		stopRouteWatch()
		stopReloadWatch()
		disposeStorage()
		if (active) {
			stopOptionalSentryTelemetry()
			stopGoogleAnalytics()
			stopHubSpotTracking()
			posthogAnalyticsAdapter.applyPrivacyChoices(false, false)
			stopReplay()
		}
	}
}
